/**
 * Active-patient selection and write gating.
 *
 * Regression guard: the onboarding wizard picked an archived patient, sending
 * the user to a read-only screen with a disabled save button and no reason.
 */

export type PatientStatus = 'active' | 'suspended' | 'archived' | string;

export interface PatientLinkLike {
  patient_id: string;
  role?: string | null;
  created_at?: string | null;
  active?: boolean | null;
  patients?: { status?: PatientStatus | null } | null;
}

function isUsable(link: PatientLinkLike): boolean {
  if (link.active === false) return false;
  const status = link.patients?.status;
  // When the status was not selected we cannot rule the patient out.
  return status === undefined || status === null || status === 'active';
}

function byCreatedAtAsc(a: PatientLinkLike, b: PatientLinkLike): number {
  return Date.parse(a.created_at ?? '') - Date.parse(b.created_at ?? '');
}

/**
 * Picks the patient the onboarding flow should target: only active links to
 * active patients, owner links preferred, oldest first.
 */
export function selectOnboardingPatientId(links: PatientLinkLike[] | null | undefined): string | null {
  const usable = (links ?? []).filter(isUsable).sort(byCreatedAtAsc);
  const owner = usable.filter(l => l.role === 'owner');
  return owner[0]?.patient_id ?? usable[0]?.patient_id ?? null;
}

export type WriteBlockReason = 'ok' | 'archived' | 'suspended' | 'no_permission';

/** Why (or whether) care configuration writes are blocked. */
export function writeBlockReason(
  patientStatus: PatientStatus | null | undefined,
  hasPermission: boolean,
): WriteBlockReason {
  if (patientStatus === 'archived') return 'archived';
  if (patientStatus === 'suspended') return 'suspended';
  if (!hasPermission) return 'no_permission';
  return 'ok';
}

export function canWrite(
  patientStatus: PatientStatus | null | undefined,
  hasPermission: boolean,
): boolean {
  return writeBlockReason(patientStatus, hasPermission) === 'ok';
}
